/**
 * React hook for Universal Clipboard
 *
 * Integrates ClipboardManager with React state and browser clipboard API.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import type { DAGStore, Identity } from '@amorphous/core';
import {
  ClipboardManager,
  createClipboard,
  getContentTypeLabel,
  isAOSSystemType,
  getChunkStatus,
} from '@amorphous/core';
import type {
  ClipboardEntry,
  PasteOptions,
  PasteResult,
  YankResult,
  ClipboardFilter,
  ExportCheckResult,
} from '@amorphous/core';

// ============================================================================
// Types
// ============================================================================

/**
 * Device info for provenance
 */
export interface DeviceInfo {
  /** Device ID (typically derived from identity) */
  deviceId: string;
  /** Human-readable device name */
  deviceAlias: string;
  /** Browser user agent */
  browserUA: string;
}

/**
 * Clipboard hook configuration
 */
export interface UseClipboardConfig {
  /** DAG store for persistence */
  dagStore: DAGStore;
  /** User identity */
  identity: Identity | null;
  /** Device info override */
  deviceInfo?: Partial<DeviceInfo>;
  /** Callback when new entry arrives from sync */
  onNewEntry?: (entry: ClipboardEntry) => void;
  /** Callback when AOS system type is pasted (boot, package, invite) */
  onSystemPaste?: (entry: ClipboardEntry, type: string) => void;
  /** Callback to fetch missing chunks from peers (for on-demand content) */
  fetchContent?: (hashes: string[]) => Promise<boolean>;
}

/**
 * Clipboard stats
 */
export interface ClipboardStats {
  totalPastes: number;
  totalBytes: number;
  byType: Record<string, number>;
}

/**
 * Download progress for on-demand content fetching
 */
export interface DownloadProgress {
  /** Entry hash being downloaded */
  entryHash: string;
  /** Progress from 0 to 1 */
  progress: number;
  /** Number of chunks downloaded */
  chunksReceived: number;
  /** Total chunks needed */
  chunksTotal: number;
  /** Status message */
  status: 'fetching' | 'downloading' | 'complete' | 'error';
  /** Error message if status is 'error' */
  error?: string;
}

/**
 * useClipboard hook result
 */
export interface UseClipboardResult {
  /** List of clipboard entries (newest first) */
  entries: ClipboardEntry[];
  /** Currently selected entry */
  selectedEntry: ClipboardEntry | null;
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: Error | null;
  /** Clipboard stats */
  stats: ClipboardStats | null;

  /** Paste content from browser clipboard */
  pasteFromBrowser: () => Promise<PasteResult | null>;
  /** Paste raw data */
  paste: (data: Uint8Array | string, options?: PasteOptions) => Promise<PasteResult>;
  /** Yank (retrieve) content by hash or index */
  yank: (hashOrIndex: string | number) => Promise<YankResult>;
  /** Copy content to browser clipboard */
  copyToBrowser: (entryHash: string) => Promise<boolean>;
  /** Download content as file */
  download: (entryHash: string, filename?: string) => Promise<boolean>;
  /** Select an entry */
  selectEntry: (entry: ClipboardEntry | null) => void;
  /** Refresh entry list */
  refresh: () => Promise<void>;
  /** Filter entries */
  filter: (filter: ClipboardFilter) => Promise<ClipboardEntry[]>;
  /** Check export permission */
  canExport: (entryHash: string) => Promise<ExportCheckResult>;
  /** Create export grant */
  grant: (contentDag: string, recipientId: string, options?: { expires?: number; maxUses?: number }) => Promise<string>;
  /** Delete a clipboard entry */
  deleteEntry: (entryHash: string) => Promise<{ success: boolean; error?: string }>;
  /** Get human-readable type label */
  getTypeLabel: (mimeType: string) => string;
  /** Check if type is AOS system type */
  isSystemType: (mimeType: string) => boolean;
  /** Current download progress (null if not downloading) */
  downloadProgress: DownloadProgress | null;
}

// ============================================================================
// Storage
// ============================================================================

const STORAGE_KEY_DEVICE = 'aos:device-info';

/**
 * Generate or retrieve device info
 */
function getDeviceInfo(identity: Identity | null, override?: Partial<DeviceInfo>): DeviceInfo {
  // Try to load from storage
  let stored: Partial<DeviceInfo> | null = null;
  try {
    const json = localStorage.getItem(STORAGE_KEY_DEVICE);
    if (json) stored = JSON.parse(json);
  } catch {
    // Ignore
  }

  const deviceId = override?.deviceId ?? stored?.deviceId ?? identity?.publicKey ?? 'anonymous';
  const deviceAlias = override?.deviceAlias ?? stored?.deviceAlias ?? generateDeviceAlias();
  const browserUA = override?.browserUA ?? navigator.userAgent.slice(0, 64);

  const info: DeviceInfo = { deviceId, deviceAlias, browserUA };

  // Save to storage
  try {
    localStorage.setItem(STORAGE_KEY_DEVICE, JSON.stringify(info));
  } catch {
    // Ignore
  }

  return info;
}

/**
 * Generate a random device alias
 */
function generateDeviceAlias(): string {
  const adjectives = ['swift', 'bright', 'calm', 'deep', 'eager', 'fair', 'glad', 'keen'];
  const nouns = ['falcon', 'river', 'mountain', 'forest', 'ocean', 'star', 'cloud', 'wind'];
  const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  return `${adj}-${noun}`;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useClipboard(config: UseClipboardConfig): UseClipboardResult {
  const { dagStore, identity, deviceInfo: deviceOverride, onNewEntry, onSystemPaste, fetchContent } = config;

  // State
  const [entries, setEntries] = useState<ClipboardEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<ClipboardEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [stats, setStats] = useState<ClipboardStats | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);

  // Refs
  const managerRef = useRef<ClipboardManager | null>(null);
  // Use refs for callbacks to avoid infinite loops when callbacks change
  const onNewEntryRef = useRef(onNewEntry);
  const onSystemPasteRef = useRef(onSystemPaste);

  // Keep refs updated
  useEffect(() => {
    onNewEntryRef.current = onNewEntry;
    onSystemPasteRef.current = onSystemPaste;
  }, [onNewEntry, onSystemPaste]);

  // Initialize clipboard manager
  useEffect(() => {
    if (!identity) {
      setLoading(false);
      return;
    }

    const deviceInfo = getDeviceInfo(identity, deviceOverride);

    const manager = createClipboard({
      dagStore,
      keyPair: identity.getKeyPair(),
      device: {
        deviceId: deviceInfo.deviceId,
        deviceAlias: deviceInfo.deviceAlias,
        browserUA: deviceInfo.browserUA,
      },
      user: {
        userId: identity.publicKey,
      },
      onNewEntry: (entry) => {
        // Update entries list
        setEntries(prev => [entry, ...prev]);
        onNewEntryRef.current?.(entry);

        // Check for system types
        const type = entry.content.content.type;
        if (isAOSSystemType(type)) {
          onSystemPasteRef.current?.(entry, type);
        }
      },
    });

    managerRef.current = manager;

    // Load initial entries
    manager.list({ limit: 100 }).then(list => {
      setEntries(list);
      setLoading(false);
    }).catch(err => {
      setError(err);
      setLoading(false);
    });

    // Load stats
    manager.getStats().then(setStats).catch(console.warn);
  }, [dagStore, identity, deviceOverride]);

  // Paste from browser clipboard
  const pasteFromBrowser = useCallback(async (): Promise<PasteResult | null> => {
    if (!managerRef.current) return null;

    try {
      const clipboardItems = await navigator.clipboard.read();

      for (const item of clipboardItems) {
        // Try to get the best available type
        for (const type of item.types) {
          const blob = await item.getType(type);
          const data = new Uint8Array(await blob.arrayBuffer());

          const result = await managerRef.current.paste(data, {
            type: type as any,
          });

          if (result.success) {
            // Refresh entries
            const list = await managerRef.current.list({ limit: 100 });
            setEntries(list);
            setStats(await managerRef.current.getStats());

            // Check for system type
            if (result.isSystemType) {
              const latest = await managerRef.current.latest();
              if (latest) {
                onSystemPaste?.(latest, result.mimeType!);
              }
            }

            return result;
          }
        }
      }

      return { success: false, error: 'No content to paste' };
    } catch (err) {
      // Fall back to text if clipboard API not fully supported
      try {
        const text = await navigator.clipboard.readText();
        if (text) {
          const result = await managerRef.current.paste(text);
          if (result.success) {
            const list = await managerRef.current.list({ limit: 100 });
            setEntries(list);
            setStats(await managerRef.current.getStats());
          }
          return result;
        }
      } catch {
        // Ignore
      }

      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to read clipboard',
      };
    }
  }, [onSystemPaste]);

  // Paste raw data
  const paste = useCallback(async (
    data: Uint8Array | string,
    options?: PasteOptions
  ): Promise<PasteResult> => {
    if (!managerRef.current) {
      return { success: false, error: 'Clipboard not initialized' };
    }

    const result = await managerRef.current.paste(data, options);

    if (result.success) {
      const list = await managerRef.current.list({ limit: 100 });
      setEntries(list);
      setStats(await managerRef.current.getStats());

      if (result.isSystemType) {
        const latest = await managerRef.current.latest();
        if (latest) {
          onSystemPaste?.(latest, result.mimeType!);
        }
      }
    }

    return result;
  }, [onSystemPaste]);

  // Yank content (with automatic on-demand chunk fetching)
  const yank = useCallback(async (hashOrIndex: string | number): Promise<YankResult> => {
    if (!managerRef.current) {
      return { success: false, error: 'Clipboard not initialized' };
    }

    // First, try to get the entry to find the manifest hash
    let entry: ClipboardEntry | undefined;
    if (typeof hashOrIndex === 'number') {
      const list = await managerRef.current.list({ limit: hashOrIndex + 1 });
      entry = list[hashOrIndex];
    } else {
      const obj = await dagStore.get(hashOrIndex);
      if (obj && obj.type === 'clipboard.entry') {
        entry = obj as ClipboardEntry;
      }
    }

    if (!entry) {
      return { success: false, error: 'Entry not found' };
    }

    // Check if we have all the chunks
    const manifestHash = entry.content.content.dag;
    const chunkStatus = await getChunkStatus(manifestHash, dagStore);
    const totalChunks = chunkStatus.manifest?.content.chunks.length ?? chunkStatus.missing.length;

    if (chunkStatus.missing.length > 0) {
      // Need to fetch missing chunks from peers
      if (!fetchContent) {
        setDownloadProgress({
          entryHash: entry.hash,
          progress: 0,
          chunksReceived: 0,
          chunksTotal: totalChunks,
          status: 'error',
          error: 'No peer connection available',
        });
        return {
          success: false,
          error: `Missing ${chunkStatus.missing.length} chunk(s). No peer connection to fetch content.`,
        };
      }

      // Set initial progress
      setDownloadProgress({
        entryHash: entry.hash,
        progress: 0,
        chunksReceived: totalChunks - chunkStatus.missing.length,
        chunksTotal: totalChunks,
        status: 'fetching',
      });

      console.log(`[Clipboard] Fetching ${chunkStatus.missing.length} missing chunks for ${entry.hash.slice(0, 8)}`);

      // Request the missing chunks
      const requested = await fetchContent(chunkStatus.missing);
      if (!requested) {
        setDownloadProgress({
          entryHash: entry.hash,
          progress: 0,
          chunksReceived: 0,
          chunksTotal: totalChunks,
          status: 'error',
          error: 'Failed to request chunks',
        });
        return {
          success: false,
          error: 'Failed to request missing chunks from peers',
        };
      }

      // Update status to downloading
      setDownloadProgress(prev => prev ? { ...prev, status: 'downloading' } : null);

      // Wait for chunks to arrive (poll with timeout)
      const maxWaitMs = 30000; // 30 seconds
      const pollIntervalMs = 500;
      const startTime = Date.now();

      while (Date.now() - startTime < maxWaitMs) {
        await new Promise(resolve => setTimeout(resolve, pollIntervalMs));

        const status = await getChunkStatus(manifestHash, dagStore);
        const received = totalChunks - status.missing.length;

        // Update progress
        setDownloadProgress({
          entryHash: entry.hash,
          progress: status.progress,
          chunksReceived: received,
          chunksTotal: totalChunks,
          status: 'downloading',
        });

        if (status.complete) {
          console.log(`[Clipboard] All chunks received for ${entry.hash.slice(0, 8)}`);
          setDownloadProgress({
            entryHash: entry.hash,
            progress: 1,
            chunksReceived: totalChunks,
            chunksTotal: totalChunks,
            status: 'complete',
          });
          break;
        }

        console.log(`[Clipboard] Chunk download progress: ${Math.round(status.progress * 100)}% (${status.missing.length} remaining)`);
      }

      // Check final status
      const finalStatus = await getChunkStatus(manifestHash, dagStore);
      if (!finalStatus.complete) {
        setDownloadProgress({
          entryHash: entry.hash,
          progress: finalStatus.progress,
          chunksReceived: totalChunks - finalStatus.missing.length,
          chunksTotal: totalChunks,
          status: 'error',
          error: 'Download timed out',
        });
        return {
          success: false,
          error: `Timeout waiting for chunks. Downloaded ${Math.round(finalStatus.progress * 100)}%`,
        };
      }

      // Clear progress after short delay (so UI can show completion)
      setTimeout(() => setDownloadProgress(null), 1500);
    }

    // Now yank should succeed
    return managerRef.current.yank(hashOrIndex);
  }, [dagStore, fetchContent]);

  // Copy to browser clipboard
  const copyToBrowser = useCallback(async (entryHash: string): Promise<boolean> => {
    if (!managerRef.current) return false;

    try {
      // Check export permission
      const check = await managerRef.current.canExport(entryHash);
      if (!check.allowed) {
        setError(new Error(check.reason ?? 'Export not permitted'));
        return false;
      }

      const result = await managerRef.current.yank(entryHash);
      if (!result.success || !result.data) {
        setError(new Error(result.error ?? 'Failed to retrieve content'));
        return false;
      }

      const entry = result.entry!;
      const mimeType = entry.content.content.type;

      // Try to write as blob first
      if (mimeType.startsWith('image/') || mimeType.startsWith('text/')) {
        try {
          // Create a clean ArrayBuffer to avoid SharedArrayBuffer issues
          const buffer = new ArrayBuffer(result.data.length);
          new Uint8Array(buffer).set(result.data);
          const blob = new Blob([buffer], { type: mimeType });
          await navigator.clipboard.write([
            new ClipboardItem({ [mimeType]: blob }),
          ]);
          return true;
        } catch {
          // Fall through to text
        }
      }

      // Fall back to text
      const text = new TextDecoder().decode(result.data);
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Copy failed'));
      return false;
    }
  }, []);

  // Download as file
  const download = useCallback(async (entryHash: string, filename?: string): Promise<boolean> => {
    if (!managerRef.current) return false;

    try {
      // Check export permission
      const check = await managerRef.current.canExport(entryHash);
      if (!check.allowed) {
        setError(new Error(check.reason ?? 'Export not permitted'));
        return false;
      }

      const result = await managerRef.current.yank(entryHash);
      if (!result.success || !result.data) {
        setError(new Error(result.error ?? 'Failed to retrieve content'));
        return false;
      }

      const entry = result.entry!;
      const mimeType = entry.content.content.type;
      const defaultFilename = entry.content.meta.filename ??
        `clipboard-${entry.content.id}${getExtension(mimeType)}`;

      // Create a clean ArrayBuffer to avoid SharedArrayBuffer issues
      const buffer = new ArrayBuffer(result.data.length);
      new Uint8Array(buffer).set(result.data);
      const blob = new Blob([buffer], { type: mimeType });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = filename ?? defaultFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Download failed'));
      return false;
    }
  }, []);

  // Select entry
  const selectEntry = useCallback((entry: ClipboardEntry | null) => {
    setSelectedEntry(entry);
  }, []);

  // Refresh list
  const refresh = useCallback(async () => {
    if (!managerRef.current) return;
    const list = await managerRef.current.list({ limit: 100 });
    setEntries(list);
    setStats(await managerRef.current.getStats());
  }, []);

  // Filter entries
  const filter = useCallback(async (f: ClipboardFilter): Promise<ClipboardEntry[]> => {
    if (!managerRef.current) return [];
    return managerRef.current.list(f);
  }, []);

  // Check export permission
  const canExport = useCallback(async (entryHash: string): Promise<ExportCheckResult> => {
    if (!managerRef.current) {
      return { allowed: false, reason: 'Clipboard not initialized' };
    }
    return managerRef.current.canExport(entryHash);
  }, []);

  // Create grant
  const grant = useCallback(async (
    contentDag: string,
    recipientId: string,
    options?: { expires?: number; maxUses?: number }
  ): Promise<string> => {
    if (!managerRef.current) throw new Error('Clipboard not initialized');
    return managerRef.current.grant(contentDag, recipientId, options);
  }, []);

  // Delete entry
  const deleteEntry = useCallback(async (entryHash: string): Promise<{ success: boolean; error?: string }> => {
    if (!managerRef.current) {
      return { success: false, error: 'Clipboard not initialized' };
    }

    const result = await managerRef.current.delete(entryHash);

    if (result.success) {
      // Remove from local state
      setEntries(prev => prev.filter(e => e.hash !== entryHash));
      // Clear selection if deleted entry was selected
      setSelectedEntry(prev => prev?.hash === entryHash ? null : prev);
      // Update stats
      managerRef.current.getStats().then(setStats).catch(console.warn);
    }

    return result;
  }, []);

  return {
    entries,
    selectedEntry,
    loading,
    error,
    stats,
    downloadProgress,
    pasteFromBrowser,
    paste,
    yank,
    copyToBrowser,
    download,
    selectEntry,
    refresh,
    filter,
    canExport,
    grant,
    deleteEntry,
    getTypeLabel: getContentTypeLabel,
    isSystemType: isAOSSystemType,
  };
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Get file extension for MIME type
 */
function getExtension(mimeType: string): string {
  const extensions: Record<string, string> = {
    'image/png': '.png',
    'image/jpeg': '.jpg',
    'image/gif': '.gif',
    'image/webp': '.webp',
    'image/svg+xml': '.svg',
    'video/webm': '.webm',
    'video/mp4': '.mp4',
    'audio/webm': '.webm',
    'audio/mpeg': '.mp3',
    'application/pdf': '.pdf',
    'application/json': '.json',
    'text/plain': '.txt',
    'text/html': '.html',
    'text/markdown': '.md',
    'text/x-python': '.py',
    'text/x-javascript': '.js',
    'text/x-typescript': '.ts',
    'text/x-rust': '.rs',
    'text/x-go': '.go',
  };
  return extensions[mimeType] ?? '';
}
